import { useCallback, useState, type ReactNode } from "react";
import {
  ScrollView,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from "react-native";
import { useTranslation } from "react-i18next";

import type { UiTheme } from "../theme";
import { IconButton } from "../primitives/IconButton";
import { Sheet } from "../primitives/Sheet";
import { hiddenFromScreenReader } from "../primitives/hiddenFromScreenReader";
import { useBreakpoint } from "../useBreakpoint";
import { InboxSection } from "./InboxSection";
import { useInboxContext } from "./InboxProvider";

// INBOX-10: the bell with a counter, in the top row of every tab and every
// role, and in the logger header. It replaces the global inbox banner.
// Antenore: «un'icona campana con il contatore di messaggi non letti,
// visibile ovunque in alto, tap e mostra tutte le notifiche».
// The count is the server's total still in the inbox, not one page's length,
// read from the shared InboxProvider (no read state, no second GET). Tap opens
// the panel OVER the current screen (bottom sheet narrow, side panel wide),
// rendering InboxSection. The number is text, never a dot alone, and the
// spoken name carries the exact count.

/** Past this the badge reads "9+"; the spoken name keeps the real number. */
const BADGE_CAP = 9;

export type NotificationBellProps = {
  theme: UiTheme;
  /** Safe-area bottom inset (px) for the narrow sheet, from the mount; 0 on web. */
  bottomInset?: number;
  /**
   * Replaces the default bare IconButton with a caller-drawn trigger (the
   * mobile experience switcher draws the bell as one of its segments). The
   * bell keeps ALL the logic: opening, the count and the spoken name arrive
   * here already made. `withBadge(glyph, size)` wraps the caller's glyph of
   * `size` px and anchors the counter to its corner, kept inside the row.
   */
  renderTrigger?: (trigger: BellTrigger) => ReactNode;
};

export type BellTrigger = {
  onPress: () => void;
  accessibilityLabel: string;
  withBadge: (glyph: ReactNode, size: number) => ReactNode;
};

export function NotificationBell({
  theme,
  bottomInset = 0,
  renderTrigger,
}: NotificationBellProps) {
  const { t } = useTranslation();
  const { wide } = useBreakpoint();
  const { height } = useWindowDimensions();
  const styles = makeStyles(theme);
  const { count, refresh } = useInboxContext();
  const [open, setOpen] = useState(false);

  // Opening re-reads the list, so the panel is not showing what the shell
  // fetched at boot. The provider keeps the rows on screen while it does.
  const onOpen = useCallback(() => {
    setOpen(true);
    void refresh();
  }, [refresh]);
  const onClose = useCallback(() => setOpen(false), []);

  // `contained` keeps the counter inside the caller's own row (the switcher
  // segment has ~5dp above its icon); the header's bell lets it rise past the
  // glyph, where the 36pt box has room.
  const badged = (contained: boolean) => (glyph: ReactNode, size: number) => (
    <View>
      {glyph}
      {count > 0 ? (
        <View
          pointerEvents="none"
          style={[
            styles.badge,
            { left: size - theme.spacing.s3 },
            contained && styles.badgeContained,
          ]}
          {...hiddenFromScreenReader()}
        >
          <Text style={styles.badgeText} numberOfLines={1}>
            {count > BADGE_CAP ? `${BADGE_CAP}+` : String(count)}
          </Text>
        </View>
      ) : null}
    </View>
  );

  return (
    <>
      {renderTrigger ? (
        renderTrigger({
          onPress: onOpen,
          accessibilityLabel: t("inbox.bell.a11y", { count }),
          withBadge: badged(true),
        })
      ) : (
        <IconButton
          theme={theme}
          name="bell"
          variant="bare"
          accessibilityLabel={t("inbox.bell.a11y", { count })}
          onPress={onOpen}
          renderGlyph={badged(false)}
        />
      )}
      <Sheet
        theme={theme}
        visible={open}
        onRequestClose={onClose}
        title={t("inbox.bell.title")}
        onClose={onClose}
        closeAccessibilityLabel={t("inbox.bell.close")}
        bottomInset={bottomInset}
        edge={wide ? "end" : "bottom"}
      >
        <ScrollView
          style={wide ? styles.scrollSide : { maxHeight: Math.round(height * 0.7) }}
          contentContainerStyle={styles.scrollBody}
        >
          <InboxSection theme={theme} onLeave={onClose} />
        </ScrollView>
      </Sheet>
    </>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, radius: r, spacing: s, typography: ty } = theme;
  // `base`, the size DESIGN.md gives glanced mono numbers (the set row's
  // weight x reps): at `sm`, the Badge floor, the digits measured 9-10px on
  // the bench. The dot grows to fit "9+" and keeps its bottom-left corner
  // where it was, so it covers no more of the bell than before.
  const digitLine = Math.round(ty.sizes.base * ty.leading.tight);
  const dotHeight = digitLine + 2 * s.s1;
  return StyleSheet.create({
    // Anchored to the glyph's top-right corner (`left` is set per glyph size)
    // and pushed past it, so the number sits off the bell's strokes. Absolute
    // and inert: the 36pt box and its hitSlop stay what they were.
    badge: {
      position: "absolute",
      top: s.s3 - dotHeight,
      minWidth: dotHeight,
      height: dotHeight,
      paddingHorizontal: s.s1,
      borderRadius: r.r4,
      backgroundColor: p.brand600,
      alignItems: "center",
      justifyContent: "center",
    },
    badgeContained: { top: -s.s1 },
    // brand600 + inkOnBrand: the pair IconButton's `primary` already uses
    // for text on brand (brand500 fails 4.5:1 on the light ground).
    badgeText: {
      color: p.inkOnBrand,
      fontFamily: ty.fonts.mono,
      fontSize: ty.sizes.base,
      lineHeight: digitLine,
      fontWeight: "700",
      fontVariant: ["tabular-nums"],
    },
    scrollSide: { flex: 1 },
    scrollBody: { paddingBottom: s.s3 },
  });
}
